import type { CrossCheckClause } from '../types/crossCheck';
import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import apiService from '../services/api';
import type { AskAgentContextSource, AskAgentDomain } from './clauseAskAgentContext';
import {
  buildSuggestEditReferenceForClause,
  getClientRequestPlainTextForAsk,
  getClientRequestXml,
} from './clauseAskAgentContext';
import {
  logSuggestEdit,
  logSuggestEditError,
  logSuggestEditWarn,
  logTextBaselineComparison,
  textSnapshot,
} from './suggestEditDebug';

/**
 * Stream an AI edit of the client request using chat / agent context as reference
 * (same engine path as past-negotiations + AI on the clause card).
 */
export async function streamAiSuggestionFromChat(
  projectId: string,
  clause: CrossCheckClause,
  messages: Array<{ role: string; content: string }>,
  options?: {
    contextSource?: AskAgentContextSource | null;
    askDomain?: AskAgentDomain;
    playbookPlainTextById?: Record<string, string>;
    playbookFileNameById?: Record<string, string>;
  },
): Promise<string | null> {
  const clauseId = clause.id;
  const clauseLabel = clause.clause_number ?? clause.id;

  logSuggestEdit('BEFORE_STREAM — start', {
    clauseId,
    clauseLabel,
    projectId,
    messageCount: messages.length,
    lastUserMessage: [...messages].reverse().find((m) => m.role === 'user')?.content?.slice(0, 200),
  });

  const { referenceText, referenceExplanation, sourceType } = buildSuggestEditReferenceForClause(
    clause,
    messages,
    options?.contextSource,
    options?.playbookPlainTextById ?? {},
    options?.playbookFileNameById ?? {},
  );
  if (!referenceText.trim()) {
    logSuggestEditWarn('BEFORE_STREAM — aborted', { reason: 'empty referenceText' });
    return null;
  }

  const crXml =
    getClientRequestXml(clause) ||
    TrackChangesParser.createMinimalParagraphOoxml(getClientRequestPlainTextForAsk(clause));
  const crXmlHasRef = /REF|MERGEFORMAT|instrText/i.test(crXml);
  const workingCrXml = crXmlHasRef
    ? TrackChangesParser.sanitizeFieldReferencesInOoxml(crXml)
    : crXml;
  // Same accepted-text semantics as RobustXmlTrackChangesGenerator (sanitized OOXML + collapse ws)
  const crText =
    TrackChangesParser.extractAcceptedText(workingCrXml).replace(/\s+/g, ' ').trim() ||
    getClientRequestPlainTextForAsk(clause) ||
    TrackChangesParser.extractText(workingCrXml);

  const crXmlAcceptedRaw = TrackChangesParser.extractAcceptedText(workingCrXml);
  const crTextLegacy = getClientRequestPlainTextForAsk(clause);

  logSuggestEdit('BEFORE_STREAM — inputs', {
    clauseId,
    contextSource: options?.contextSource ?? null,
    sourceType,
    crXmlLen: crXml.length,
    crXmlHasRef,
    crText: textSnapshot(crText),
    crXmlAcceptedRaw: textSnapshot(crXmlAcceptedRaw),
    referenceTextLen: referenceText.length,
    referenceTextPreview: referenceText.slice(0, 400),
    referenceExplanationPreview: referenceExplanation.slice(0, 400),
  });

  logTextBaselineComparison('apiCrText (sent to engine)', crText, 'extractAcceptedText(crXml)', crXmlAcceptedRaw);
  if (crTextLegacy && crTextLegacy.replace(/\s+/g, ' ').trim() !== crText) {
    logSuggestEditWarn('BEFORE_STREAM — crText differs from legacy getClientRequestPlainTextForAsk', {
      legacySnapshot: textSnapshot(crTextLegacy),
      unifiedSnapshot: textSnapshot(crText),
    });
  }

  let accumulatedText = '';
  let finalSuggestedText = '';
  let chunkCount = 0;

  for await (const chunk of apiService.generateBankOfChangeSuggestionStream(
    projectId,
    crText,
    crXml,
    referenceText,
    sourceType,
    referenceExplanation,
  )) {
    chunkCount += 1;
    if (chunk.type === 'text' && chunk.content) {
      accumulatedText += chunk.content;
    } else if (chunk.type === 'complete') {
      finalSuggestedText = chunk.suggested_text ?? accumulatedText;
      logSuggestEdit('STREAM — complete chunk', {
        clauseId,
        suggestedFromField: Boolean(chunk.suggested_text),
        accumulatedLen: accumulatedText.length,
        finalLen: finalSuggestedText.length,
        finalSnapshot: textSnapshot(finalSuggestedText),
      });
    } else if (chunk.type === 'error') {
      logSuggestEditError('STREAM — error chunk', { clauseId, error: chunk.error });
      throw new Error(chunk.error);
    }
  }

  logSuggestEdit('AFTER_STREAM — raw suggestion', {
    clauseId,
    chunkCount,
    accumulatedSnapshot: textSnapshot(accumulatedText),
    finalSnapshot: textSnapshot(finalSuggestedText),
  });

  if (!finalSuggestedText?.trim()) {
    logSuggestEditWarn('AFTER_STREAM — aborted', { reason: 'empty finalSuggestedText' });
    return null;
  }

  logTextBaselineComparison(
    'finalSuggestedText (engine output)',
    finalSuggestedText,
    'apiCrText (diff should be against OOXML accepted, not this)',
    crText,
  );

  const { RobustXmlTrackChangesGenerator } = await import(
    '../features/rephrasing/RobustXmlTrackChangesGenerator'
  );

  logSuggestEdit('BEFORE_TRACK_CHANGES_XML', {
    clauseId,
    originalOoxmlLen: crXml.length,
    newTextSnapshot: textSnapshot(finalSuggestedText),
  });

  const suggestedXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
    workingCrXml,
    finalSuggestedText,
    'AI Suggestion',
    { source: 'streamAiSuggestionFromChat', clauseId, clauseLabel },
  );

  const acceptedAfter = TrackChangesParser.extractAcceptedText(suggestedXml).replace(/\s+/g, ' ').trim();
  const intendedAfter = finalSuggestedText.replace(/\s+/g, ' ').trim();

  logSuggestEdit('AFTER_TRACK_CHANGES_XML', {
    clauseId,
    suggestedXmlLen: suggestedXml.length,
    acceptedAfterSnapshot: textSnapshot(acceptedAfter),
    intendedAfterSnapshot: textSnapshot(intendedAfter),
    acceptedMatchesIntended: acceptedAfter === intendedAfter,
    hasNewIns: suggestedXml.includes('AI Suggestion'),
    hasDel: /<w:del\b/i.test(suggestedXml),
  });

  if (acceptedAfter !== intendedAfter) {
    logSuggestEditWarn('AFTER_TRACK_CHANGES_XML — accepted text ≠ engine suggestion', {
      clauseId,
      hint: 'Track-changes apply/diff may be misaligned; check DIFF_OPS_DETAIL and TEXT_BASELINE_MISMATCH logs.',
      acceptedPreview: acceptedAfter.slice(0, 200),
      intendedPreview: intendedAfter.slice(0, 200),
    });
  }

  return suggestedXml;
}
